import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import InfoTooltip from '../components/InfoTooltip';
import {
  Boxes, Plus, RefreshCw, Trash2, Pencil, Zap, Play, CheckCircle,
  XCircle, AlertCircle, X, Loader2, ChevronRight,
  Wifi, WifiOff,
} from 'lucide-react';

const POLL_INTERVAL = 15000;

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function daysFromSeconds(sec) {
  if (sec == null) return null;
  return Math.round(sec / 86400);
}

function isValidUrl(value) {
  if (!value) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

const COLLECTION_STATE_LABEL = { OK: 'OK', DEGRADED: 'Degraded', UNKNOWN: 'Unknown', ERROR: 'Error' };
const COLLECTION_STATE_CLASS = { OK: 'healthy', DEGRADED: 'warning', UNKNOWN: 'unknown', ERROR: 'critical' };

function CollectionStateBadge({ state }) {
  const cls = COLLECTION_STATE_CLASS[state] || 'unknown';
  return <span className={`ow-badge ${cls}`}><span className={`ow-dot ${cls}`} />{COLLECTION_STATE_LABEL[state] || state}</span>;
}

function TransportBadge({ instance }) {
  if (instance.connectionMethod === 'AGENT_TUNNEL') {
    const offline = instance.agentStatus && instance.agentStatus !== 'ONLINE';
    return (
      <span className={`ow-transport-badge${offline ? ' agent-offline' : ''}`}>
        {offline ? <WifiOff size={12} /> : <Wifi size={12} />}
        Agent tunnel {instance.agentStatus ? `(agent ${instance.agentStatus.toLowerCase()})` : ''}
      </span>
    );
  }
  return <span className="ow-transport-badge">Direct</span>;
}

/* ── Phase 4a, Task D: the two agent roles, visible together ──
   An instance can have a tunnel agent (network transport to the site,
   TransportBadge above) and a separate host agent (installed directly on
   the appliance, GPU/disk/hardware detail) doing different jobs. Nothing
   rendered when unbound - most instances legitimately have no host
   agent, and this list already reads as busy without an empty badge on
   every row. */
function HostAgentBadge({ instance }) {
  if (!instance.hostAgentId) return null;
  const offline = instance.hostAgentStatus && instance.hostAgentStatus !== 'ONLINE';
  return (
    <span className={`ow-transport-badge${offline ? ' agent-offline' : ''}`}>
      {offline ? <WifiOff size={12} /> : <Wifi size={12} />}
      Host agent {instance.hostAgentName ? `(${instance.hostAgentName})` : ''}
      <InfoTooltip text="An agent installed on the appliance itself, which reports GPU, full disk and hardware detail that cannot be read remotely. Separate from the tunnel agent above that carries network traffic." />
    </span>
  );
}

function TestConnectionPanel({ result }) {
  if (!result) return null;
  const rows = [
    { key: 'rancher', label: 'Rancher', detail: result.rancher?.ok ? `Cluster ${result.rancher.clusterId || ''}` : result.rancher?.error },
    { key: 'prometheus', label: 'Prometheus', detail: result.prometheus?.ok ? `Retention ${daysFromSeconds(result.prometheus.retentionSec) ?? '?'}d` : result.prometheus?.error },
    { key: 'k8s', label: 'Kubernetes', detail: result.k8s?.ok ? `${result.k8s.nodeCount ?? '?'} node(s)` : result.k8s?.error },
    // App API tier (Phase 2c): a real probe now, not a placeholder. Treated
    // the same as the three rows above, not specially muted, since a red X
    // here is exactly as actionable as a red X on Rancher.
    { key: 'app', label: 'App API', detail: result.app?.ok ? `${result.app.serviceCount ?? '?'} service(s)` : result.app?.error },
  ];

  return (
    <div className="ow-test-grid">
      {result.transport && (
        <div className="ow-test-row-detail" style={{ marginBottom: 2 }}>
          Transport attempted: {result.transport.mode === 'AGENT_TUNNEL' ? `Agent tunnel (${result.transport.agentId})` : 'Direct'}
        </div>
      )}
      {rows.map((row) => (
        <div className="ow-test-row" key={row.key}>
          <span className="ow-test-row-label">
            {row.detail && result[row.key]?.ok ? <CheckCircle size={14} color="var(--status-ok)" /> : <XCircle size={14} color="var(--status-error)" />}
            {row.label}
          </span>
          <span className="ow-test-row-detail">{row.detail || '—'}</span>
        </div>
      ))}
    </div>
  );
}

const SECRET_FIELDS = [
  { field: 'appPassword', clearField: 'clearAppPassword', hasField: 'hasAppPassword', label: 'App Password' },
  { field: 'appApiKey', clearField: 'clearAppApiKey', hasField: 'hasAppApiKey', label: 'App API Key' },
  { field: 'rancherToken', clearField: 'clearRancherToken', hasField: 'hasRancherToken', label: 'Rancher Token' },
  { field: 'k8sToken', clearField: 'clearK8sToken', hasField: 'hasK8sToken', label: 'Kubernetes ServiceAccount Token' },
];

function emptyForm() {
  return {
    name: '', enabled: true,
    appBaseUrl: '', appUsername: '', appPassword: '', appApiKey: '',
    rancherBaseUrl: '', rancherToken: '',
    k8sToken: '',
    tier: 'RANCHER_PROXY', pollIntervalSec: 60, stalenessSec: 300, verifyTls: false,
    clearAppPassword: false, clearAppApiKey: false, clearRancherToken: false, clearK8sToken: false,
    hasAppPassword: false, hasAppApiKey: false, hasRancherToken: false, hasK8sToken: false,
  };
}

function formFromInstance(instance) {
  return {
    name: instance.name || '',
    enabled: !!instance.enabled,
    appBaseUrl: instance.appBaseUrl || '',
    appUsername: instance.appUsername || '',
    appPassword: '',
    appApiKey: '',
    rancherBaseUrl: instance.rancherBaseUrl || '',
    rancherToken: '',
    k8sToken: '',
    tier: instance.tier || 'RANCHER_PROXY',
    pollIntervalSec: instance.pollIntervalSec ?? 60,
    stalenessSec: instance.stalenessSec ?? 300,
    verifyTls: !!instance.verifyTls,
    clearAppPassword: false, clearAppApiKey: false, clearRancherToken: false, clearK8sToken: false,
    hasAppPassword: !!instance.hasAppPassword,
    hasAppApiKey: !!instance.hasAppApiKey,
    hasRancherToken: !!instance.hasRancherToken,
    hasK8sToken: !!instance.hasK8sToken,
  };
}

function validateForm(form) {
  const errors = {};
  if (!form.name || !form.name.trim()) errors.name = 'Name is required';
  if (!form.appBaseUrl || !form.appBaseUrl.trim()) errors.appBaseUrl = 'App base URL is required';
  else if (!isValidUrl(form.appBaseUrl)) errors.appBaseUrl = 'Must be a valid URL';
  if (form.rancherBaseUrl && !isValidUrl(form.rancherBaseUrl)) errors.rancherBaseUrl = 'Must be a valid URL';

  // Kubernetes and Prometheus collection cannot work without k8sToken on
  // this tier: it is the only credential CCM sends for either, by design
  // (see docs/onwatch/README.md). "Effective" accounts for a pending clear
  // or a freshly typed value, not just what was already saved.
  const effectiveHasK8sToken = form.clearK8sToken ? false : (form.k8sToken.trim() ? true : form.hasK8sToken);
  if (form.tier === 'RANCHER_PROXY' && !effectiveHasK8sToken) {
    errors.k8sToken = 'Required for the Rancher Proxy tier: Kubernetes and Prometheus collection cannot work without it';
  }

  const poll = Number(form.pollIntervalSec);
  const staleness = Number(form.stalenessSec);
  if (!Number.isFinite(poll) || poll < 30) errors.pollIntervalSec = 'Must be at least 30 seconds';
  if (!Number.isFinite(staleness) || staleness < 60) errors.stalenessSec = 'Must be at least 60 seconds';
  if (Number.isFinite(poll) && Number.isFinite(staleness) && staleness <= poll) {
    errors.stalenessSec = 'Must exceed the poll interval, otherwise an instance is stale before it can ever poll';
  }
  return errors;
}

function buildPayload(form) {
  const payload = {
    name: form.name.trim(),
    enabled: form.enabled,
    appBaseUrl: form.appBaseUrl.trim(),
    appUsername: form.appUsername.trim() || null,
    rancherBaseUrl: form.rancherBaseUrl.trim() || null,
    tier: form.tier,
    pollIntervalSec: Number(form.pollIntervalSec),
    stalenessSec: Number(form.stalenessSec),
    verifyTls: form.verifyTls,
  };
  for (const { field, clearField } of SECRET_FIELDS) {
    if (form[clearField]) {
      payload[clearField] = true;
    } else if (form[field]) {
      // An empty secret field means leave unchanged; never send an empty string.
      payload[field] = form[field];
    }
  }
  return payload;
}

function InstanceFormModal({ instance, onClose, onSaved, showToast }) {
  const isEdit = !!instance;
  const [form, setForm] = useState(() => (instance ? formFromInstance(instance) : emptyForm()));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const toggleClear = (clearField, field) => {
    setForm((f) => ({ ...f, [clearField]: !f[clearField], [field]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(form);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    try {
      const url = isEdit ? `/api/onwatch/instances/${instance.id}` : '/api/onwatch/instances';
      const res = await fetchWithTenant(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(form)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (data.details) setErrors(data.details);
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      showToast(isEdit ? 'Instance updated' : 'Instance created');
      onSaved();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit OnWatch Instance' : 'Add OnWatch Instance'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="ow-form-section">
              <div className="ow-form-section-title">Basics</div>
              <div className="ow-form-row">
                <label>Name</label>
                <input className="form-input" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Charleston Main" />
                {errors.name && <span className="ow-form-error">{errors.name}</span>}
              </div>
              <div className="ow-form-row ow-form-row-inline">
                <label style={{ margin: 0 }}>Enabled</label>
                <input type="checkbox" checked={form.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
              </div>
            </div>

            <div className="ow-form-section">
              <div className="ow-form-section-title">App Tier (OnWatch REST API)</div>
              <div className="ow-form-row">
                <label>Base URL</label>
                <input className="form-input" value={form.appBaseUrl} onChange={(e) => setField('appBaseUrl', e.target.value)} placeholder="https://192.168.1.111" />
                {errors.appBaseUrl && <span className="ow-form-error">{errors.appBaseUrl}</span>}
              </div>
              <SecretRow label="API Key" field="appApiKey" clearField="clearAppApiKey" hasField="hasAppApiKey" form={form} setField={setField} toggleClear={toggleClear} />
              <div className="ow-form-help">
                Preferred over username and password below when set: survives password rotation and is
                revocable independently. Leave username/password blank once an API key is configured.
              </div>
              <div className="ow-form-row">
                <label>Username</label>
                <input className="form-input" value={form.appUsername} onChange={(e) => setField('appUsername', e.target.value)} placeholder="Administrator" />
              </div>
              <SecretRow label="Password" field="appPassword" clearField="clearAppPassword" hasField="hasAppPassword" form={form} setField={setField} toggleClear={toggleClear} />
            </div>

            <div className="ow-form-section">
              <div className="ow-form-section-title">Rancher Proxy</div>
              <div className="ow-form-row">
                <label>Base URL</label>
                <input className="form-input" value={form.rancherBaseUrl} onChange={(e) => setField('rancherBaseUrl', e.target.value)} placeholder="https://192.168.1.166:9443" />
                {errors.rancherBaseUrl && <span className="ow-form-error">{errors.rancherBaseUrl}</span>}
              </div>
              <SecretRow label="Rancher API Token" field="rancherToken" clearField="clearRancherToken" hasField="hasRancherToken" form={form} setField={setField} toggleClear={toggleClear} />
            </div>

            <div className="ow-form-section">
              <div className="ow-form-section-title">Kubernetes</div>
              <SecretRow
                label={form.tier === 'RANCHER_PROXY' ? 'ServiceAccount Token *' : 'ServiceAccount Token'}
                field="k8sToken"
                clearField="clearK8sToken"
                hasField="hasK8sToken"
                form={form}
                setField={setField}
                toggleClear={toggleClear}
              />
              {errors.k8sToken && <span className="ow-form-error">{errors.k8sToken}</span>}
              <div className="ow-form-help">
                The read-only ServiceAccount token from{' '}
                <a href="https://github.com/Serverli-Inc/serverliccm/blob/pre-prod/docs/onwatch/serverli-monitor-rbac.yaml" target="_blank" rel="noreferrer">
                  docs/onwatch/serverli-monitor-rbac.yaml
                </a>. This is a different credential from the Rancher API token above: it is the
                only credential used for Kubernetes and Prometheus collection, and is required for
                the Rancher Proxy tier.
              </div>
            </div>

            <div className="ow-form-section">
              <div className="ow-form-section-title">Advanced</div>
              <div className="ow-form-row">
                <label>Tier</label>
                <select className="form-select" value={form.tier} onChange={(e) => setField('tier', e.target.value)}>
                  <option value="RANCHER_PROXY">Rancher Proxy</option>
                  <option value="AGENT">Agent</option>
                  <option value="DIRECT_API">Direct API</option>
                </select>
              </div>
              <div className="ow-form-grid-2">
                <div className="ow-form-row">
                  <label>Poll Interval (sec)</label>
                  <input className="form-input" type="number" min={30} value={form.pollIntervalSec} onChange={(e) => setField('pollIntervalSec', e.target.value)} />
                  {errors.pollIntervalSec && <span className="ow-form-error">{errors.pollIntervalSec}</span>}
                </div>
                <div className="ow-form-row">
                  <label>Staleness (sec)</label>
                  <input className="form-input" type="number" min={60} value={form.stalenessSec} onChange={(e) => setField('stalenessSec', e.target.value)} />
                  {errors.stalenessSec && <span className="ow-form-error">{errors.stalenessSec}</span>}
                </div>
              </div>
              <div className="ow-form-row ow-form-row-inline">
                <label style={{ margin: 0 }}>Verify TLS</label>
                <input type="checkbox" checked={form.verifyTls} onChange={(e) => setField('verifyTls', e.target.checked)} />
              </div>
              <div className="ow-form-help">OnWatch appliances ship self-signed certificates. Leave unchecked unless this instance has a real certificate.</div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-accent" disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : null} {isEdit ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SecretRow({ label, field, clearField, hasField, form, setField, toggleClear }) {
  const has = form[hasField];
  const pending = form[clearField];
  const placeholder = pending ? 'will be cleared' : has ? 'unchanged' : 'not set';
  return (
    <div className="ow-form-row">
      <label>{label}</label>
      <div className="ow-secret-field">
        <input
          className="form-input"
          type="password"
          value={form[field]}
          disabled={pending}
          onChange={(e) => setField(field, e.target.value)}
          placeholder={placeholder}
          autoComplete="new-password"
        />
        {has && (
          <button
            type="button"
            className={`ow-secret-clear-btn${pending ? ' pending' : ''}`}
            onClick={() => toggleClear(clearField, field)}
          >
            {pending ? 'Undo' : 'Clear'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function OnWatchInstances() {
  const navigate = useNavigate();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [modalInstance, setModalInstance] = useState(undefined); // undefined = closed, null = create, object = edit
  const [testResults, setTestResults] = useState({});
  const [testingId, setTestingId] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchInstances = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetchWithTenant('/api/onwatch/instances');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // childCounts now comes directly on the list response (Phase 2d Item
      // 1), no per-instance backfill call needed.
      setInstances(data.instances || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstances();
    const timer = setInterval(() => fetchInstances(true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchInstances]);

  const handleTest = async (id) => {
    setTestingId(id);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/test`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setTestResults((prev) => ({ ...prev, [id]: data }));
      fetchInstances(true);
    } catch (e) {
      showToast(`Test connection failed: ${e.message}`, 'error');
    } finally {
      setTestingId(null);
    }
  };

  const handleForcePoll = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}/poll`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Poll complete');
      fetchInstances(true);
    } catch (e) {
      showToast(`Force poll failed: ${e.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete "${name}"? This stops monitoring and deletes all collected history.`)) return;
    setActionLoading(id);
    try {
      const res = await fetchWithTenant(`/api/onwatch/instances/${id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error(`HTTP ${res.status}`);
      showToast('Instance deleted');
      fetchInstances(true);
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="page-content">
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          padding: '12px 18px', borderRadius: 8,
          background: toast.type === 'error' ? 'var(--status-error-bg)' : 'var(--status-ok-bg)',
          color: toast.type === 'error' ? 'var(--status-error-text)' : 'var(--status-ok-text)',
          fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {toast.type === 'error' ? <AlertCircle size={15} /> : <CheckCircle size={15} />}
          {toast.msg}
        </div>
      )}

      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title"><Boxes size={26} /> OnWatch</h1>
          <p className="page-subtitle">Oosto OnWatch / OnAccess cluster monitoring instances</p>
        </div>
        <div className="ow-controls">
          <button className="btn btn-secondary btn-sm" onClick={() => fetchInstances()} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="btn btn-accent" onClick={() => setModalInstance(null)}>
            <Plus size={16} /> Add Instance
          </button>
        </div>
      </div>

      {error && (
        <div className="ow-form-error" style={{ marginBottom: 16 }}>{error}</div>
      )}

      {loading ? (
        <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading instances...</p></div>
      ) : instances.length === 0 ? (
        <div className="ow-empty">
          <Boxes size={48} />
          <h3>No OnWatch instances</h3>
          <p>An instance is auto-provisioned when an Oosto OnWatch integration is created, or add one manually below.</p>
          <button className="btn btn-accent" onClick={() => setModalInstance(null)}><Plus size={16} /> Add Instance</button>
        </div>
      ) : (
        <div className="ow-list">
          {instances.map((instance) => (
            <div key={instance.id}>
              <div className="ow-instance-row" onClick={() => navigate(`/monitoring/onwatch/${instance.id}`)}>
                <div className="ow-instance-main">
                  <div className="ow-instance-name">
                    {instance.name}
                    <CollectionStateBadge state={instance.collectionState} />
                    {!instance.enabled && <span className="ow-badge unknown">Disabled</span>}
                  </div>
                  <div className="ow-instance-meta">
                    <TransportBadge instance={instance} />
                    <HostAgentBadge instance={instance} />
                    <span>Last success {timeAgo(instance.lastSuccessAt)}</span>
                    {instance.lastError && <span className="ow-health-critical">{instance.lastError}</span>}
                  </div>
                </div>
                <div className="ow-instance-stats">
                  <div className="ow-instance-stat">
                    <div className="ow-instance-stat-value">{instance.childCounts?.nodes ?? '—'}</div>
                    <div className="ow-instance-stat-label">Nodes</div>
                  </div>
                  <div className="ow-instance-stat">
                    <div className="ow-instance-stat-value">{instance.childCounts?.workloads ?? '—'}</div>
                    <div className="ow-instance-stat-label">Workloads</div>
                  </div>
                </div>
                <div className="ow-instance-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleTest(instance.id)} disabled={testingId === instance.id} title="Test connection">
                    {testingId === instance.id ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleForcePoll(instance.id)} disabled={actionLoading === instance.id} title="Force poll">
                    <Play size={14} />
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setModalInstance(instance)} title="Edit">
                    <Pencil size={14} />
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleDelete(instance.id, instance.name)} disabled={actionLoading === instance.id} title="Delete">
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={16} color="var(--text-tertiary)" />
                </div>
              </div>
              {testResults[instance.id] && (
                <div style={{ padding: '4px 4px 12px' }}>
                  <TestConnectionPanel result={testResults[instance.id]} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalInstance !== undefined && (
        <InstanceFormModal
          instance={modalInstance}
          showToast={showToast}
          onClose={() => setModalInstance(undefined)}
          onSaved={() => { setModalInstance(undefined); fetchInstances(true); }}
        />
      )}
    </div>
  );
}
